import { useMemo } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { ChevronDown, LogOut, Moon, Settings, Sun } from "lucide-react";

import { useTheme } from "../../context/ThemeContext";
import {
  Breadcrumb,
  Menu,
  MenuContent,
  MenuItem,
  MenuLabel,
  MenuSeparator,
  MenuTrigger,
  cn,
  focusRing,
} from "../../components/ui";
import { supplierBreadcrumb } from "../navigation/supplierNavigation";
import SupplierNotificationBell from "./SupplierNotificationBell";

export default function SupplierTopbar({ user, suppliers, onLogout }) {
  const { pathname } = useLocation();
  const { isDark, toggleTheme } = useTheme();
  const crumbs = useMemo(() => supplierBreadcrumb(pathname), [pathname]);
  const supplierName = suppliers?.[0]?.name || user?.email || "Supplier";
  const nameParts = supplierName.trim().split(/\s+/).filter(Boolean);
  const initials = (nameParts.length > 1
    ? nameParts.slice(0, 2).map((part) => Array.from(part)[0]).join("")
    : Array.from(supplierName).slice(0, 2).join("")).toUpperCase();
  const roleLabel = String(suppliers?.[0]?.role || "Supplier")
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

  return (
    <div className="flex h-full items-center justify-between gap-3">
      <Breadcrumb items={crumbs} className="flex-1" />

      <div className="flex shrink-0 items-center gap-1">
        <SupplierNotificationBell />

        <Menu modal={false}>
          <MenuTrigger asChild>
            <button
              type="button"
              aria-label={`Account menu: ${supplierName}, ${roleLabel}`}
              className={cn(
                "group flex h-9 items-center gap-2 rounded-control pl-1 pr-1.5 text-left transition-colors hover:bg-hover max-md:h-11",
                focusRing
              )}
            >
              <span aria-hidden="true" className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-surface-sunken text-meta font-semibold text-secondary">
                {initials}
              </span>
              <span aria-hidden="true" className="hidden min-w-0 lg:block">
                <span className="block max-w-40 truncate text-body-sm font-medium text-foreground">{supplierName}</span>
              </span>
              <span className="sr-only">{roleLabel}</span>
              <ChevronDown aria-hidden="true" className="hidden h-3.5 w-3.5 shrink-0 text-tertiary transition-transform group-data-[state=open]:rotate-180 sm:block" />
            </button>
          </MenuTrigger>

          <MenuContent className="w-64">
            <MenuLabel className="py-2">
              <span className="block truncate text-body-sm font-medium text-foreground">{supplierName}</span>
              <span className="block text-meta text-tertiary">{roleLabel}</span>
            </MenuLabel>
            <MenuSeparator />
            <MenuItem asChild>
              <NavLink to="/supplier/settings" className="gap-2">
                <Settings aria-hidden="true" className="h-4 w-4 shrink-0 text-tertiary" />
                Settings
              </NavLink>
            </MenuItem>
            <MenuSeparator />
            <MenuItem
              icon={isDark ? Sun : Moon}
              onSelect={(event) => {
                event.preventDefault();
                toggleTheme();
              }}
            >
              {isDark ? "Use light theme" : "Use dark theme"}
            </MenuItem>
            <MenuSeparator />
            <MenuItem tone="danger" icon={LogOut} onSelect={onLogout}>
              Log out
            </MenuItem>
          </MenuContent>
        </Menu>
      </div>
    </div>
  );
}
